/* eslint-disable @typescript-eslint/no-explicit-any */
import { defaultFeatureFlags } from '../../../../../backend/config/settings/featureFlags';
import { handleErrorDialog, showAlertDialog, showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { LayoutRows2 } from '@juvare/common-web-ui/LayoutRows2';
import { MenuButton } from '@juvare/common-web-ui/MenuButton';
import { handleErrorSnackbar, showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Box, Button, Divider, Typography } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { useAlertConfig, useAlertStorageClient, useAlertUiTranslation } from '../../../../hooks';
import { useAlertServiceClient } from '../../hooks';
import { AlertReviewForm } from '../AlertReviewForm';
import { AlertWizard } from '../AlertWizard';
import { TemplateCards } from '../TemplateCards';
import { FormConfigContextProvider, FormErrorDialog } from './Components';
import { applyTemplateVariablesToFormData, mapToAlertWizardFormData, normalizeNotificationSender, showTemplateEditDialog } from './Utils';
export function showNotificationDialog(params) {
    showCustomDialog({
        fullScreen: true,
        content: (close, ariaLabelledBy) => {
            const handleClose = () => {
                if (params.onClose) {
                    params.onClose(close);
                }
                else {
                    close();
                }
            };
            const handleSubmitSuccess = (data) => {
                params.onSubmitSuccess(data, handleClose);
            };
            return (React.createElement(AlertDialogContent, Object.assign({}, params, { onSubmitSuccess: handleSubmitSuccess, close: handleClose, ariaLabelledBy: ariaLabelledBy })));
        },
    });
}
export const AlertDialogContent = (props) => {
    var _a, _b;
    const { t } = useAlertUiTranslation();
    const { notificationOptions } = useAlertConfig();
    const userState = useUserState();
    const settings = useSelector((state) => (userState ? getSettings(state) : undefined));
    const [alertId, setAlertId] = React.useState(props.alertId);
    const [step, setStep] = React.useState((_a = props.dialogStep) !== null && _a !== void 0 ? _a : 'templates');
    const [userRight, setUserRight] = React.useState(undefined);
    const getInitialWizardData = () => {
        var _a, _b;
        if (alertId) {
            return undefined;
        }
        else if (props.partialAlert) {
            const channels = [];
            if ((_a = notificationOptions === null || notificationOptions === void 0 ? void 0 : notificationOptions.mandatoryChannels) === null || _a === void 0 ? void 0 : _a.length) {
                channels.push(...notificationOptions.mandatoryChannels);
            }
            const partialAlert = Object.assign(Object.assign({}, props.partialAlert), { channels });
            if (props.mode === 'notification' && props.templateVariables) {
                applyTemplateVariablesToFormData(partialAlert, props.templateVariables);
            }
            return partialAlert;
        }
        else {
            const formData = {
                responseOptions: [{ text: settings === null || settings === void 0 ? void 0 : settings.notificationOptions.responseOption }],
                type: props.mode === 'template' ? 'template' : undefined,
            };
            if ((_b = notificationOptions === null || notificationOptions === void 0 ? void 0 : notificationOptions.mandatoryChannels) === null || _b === void 0 ? void 0 : _b.length) {
                formData.channels = notificationOptions.mandatoryChannels;
            }
            return formData;
        }
    };
    const wizardData = React.useRef(getInitialWizardData());
    const alertServiceClient = useAlertServiceClient();
    const alertStorageClient = useAlertStorageClient();
    const handleSaveDraft = (saveAsNewDraft) => {
        var _a;
        const [isValid, errors] = validateWizardRequiredData(wizardData.current);
        if (!isValid) {
            showAlertDialog({
                title: t(`pages.tables.details.informationRequired`),
                content: React.createElement(FormErrorDialog, { errorMessages: errors }),
            });
        }
        else {
            if (wizardData.current) {
                alertStorageClient
                    .saveAlert(Object.assign(Object.assign({}, wizardData.current), { id: saveAsNewDraft ? undefined : (_a = wizardData.current) === null || _a === void 0 ? void 0 : _a.id, type: 'draft' }))
                    .then(() => {
                    showAlertSnackbar({
                        message: t(`pages.edit.recipients.draftSaved`),
                        type: 'success',
                        autoHideDuration: 1000,
                    });
                })
                    .catch(handleErrorDialog)
                    .finally(() => props.close());
            }
        }
    };
    const handleSaveAsTemplate = (saveExistingTemplate) => {
        const [isValid, errors] = validateWizardRequiredData(wizardData.current);
        if (!isValid) {
            showAlertDialog({
                title: t(`pages.tables.details.informationRequired`),
                content: React.createElement(FormErrorDialog, { errorMessages: errors }),
            });
        }
        else {
            showTemplateEditDialog(alertId && saveExistingTemplate
                ? wizardData.current
                : Object.assign(Object.assign({}, wizardData.current), { id: undefined, title: undefined, icon: undefined, color: undefined, pinned: false, permissions: undefined }), userState, t, alertServiceClient, alertStorageClient, saveExistingTemplate ? props.onSubmitSuccess : undefined);
        }
    };
    const handleSaveTemplate = (data, closeDialog) => {
        if (alertId != null) {
            alertStorageClient
                .updateAlert(Object.assign(Object.assign({}, data), { type: 'template' }))
                .then((response) => {
                showAlertSnackbar({
                    message: t('pages.edit.snackbar.template.updated'),
                    type: 'success',
                    autoHideDuration: 1000,
                });
                if (closeDialog) {
                    props.close();
                }
                return response;
            })
                .catch(handleErrorDialog);
        }
    };
    const menuItems = [
        {
            label: alertId != null && props.mode === 'draft' ? t(`common.saveDraftChanges`) : t(`pages.edit.recipients.saveAsDraft`),
            onClick: () => handleSaveDraft(props.mode !== 'draft'),
            'data-test': 'menu-item-save-as-draft',
        },
        {
            label: t(`pages.edit.recipients.saveAsNewTemplate`),
            onClick: () => handleSaveAsTemplate(false),
            'data-test': 'menu-item-save-as-new-template',
        },
    ];
    if (alertId != null && props.mode !== 'draft' && userRight === 'write') {
        menuItems.splice(1, 0, {
            label: t(`pages.edit.recipients.saveTemplateChanges`),
            onClick: () => { var _a; return handleSaveTemplate((_a = wizardData.current) !== null && _a !== void 0 ? _a : {}); },
            'data-test': 'menu-item-save-template-changes',
        });
    }
    else if (alertId != null && props.mode === 'draft') {
        menuItems.splice(1, 0, {
            label: t(`pages.edit.recipients.saveAsNewDraft`),
            onClick: () => handleSaveDraft(true),
            'data-test': 'menu-item-save-as-new-draft',
        });
    }
    if (alertId != null && props.mode === 'template') {
        menuItems.splice(0, 1);
    }
    const dialogHeaderName = step !== 'review' ? props.wizardHeader : props.reviewFormHeader;
    return (React.createElement(FormConfigContextProvider, { accountsTableCustomization: props.accountsTableCustomization, additionalRichTextEditorMenuItems: props.additionalRichTextEditorMenuItems },
        React.createElement(LayoutRows2, { row1: React.createElement(Box, { sx: {
                    alignItems: 'center',
                    backgroundColor: (theme) => theme.palette.grey[900],
                    color: (theme) => theme.palette.background.paper,
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 5,
                    py: 2,
                } },
                React.createElement(Typography, { id: props.ariaLabelledBy, "aria-label": dialogHeaderName, variant: 'h6' }, dialogHeaderName),
                React.createElement(Box, { sx: {
                        display: 'flex',
                        justifyContent: 'end',
                        alignItems: 'center',
                    } },
                    step === 'wizard' && (React.createElement(React.Fragment, null,
                        React.createElement(MenuButton, { color: 'inherit', variant: 'text', startIcon: React.createElement(SaveOutlinedIcon, null), endIcon: React.createElement(ArrowDropDownOutlinedIcon, null), menuItems: menuItems }, t(`pages.edit.save`)),
                        React.createElement(Divider, { orientation: 'vertical', flexItem: true, sx: {
                                width: 0,
                                height: 32,
                                borderLeftWidth: '1px',
                                borderLeftStyle: 'solid',
                                borderLeftColor: themeColors.border.boldest,
                                mx: 2,
                            } }))),
                    React.createElement(Button, { variant: 'text', color: 'inherit', size: 'large', onClick: () => props.close(), sx: { p: 0 }, "data-test": 'cancel-button' }, (_b = props.cancelButtonLabel) !== null && _b !== void 0 ? _b : t(`common.cancel`)))), row2: React.createElement(Content, Object.assign({}, props, { wizardData: wizardData, alertId: alertId, setAlertId: setAlertId, userRight: userRight, step: step, onStepChange: setStep, onDataChange: (data, userRight) => {
                    if (userRight) {
                        setUserRight(userRight);
                    }
                    wizardData.current = data;
                }, onSaveTemplate: () => handleSaveAsTemplate(true) })), fullHeightRow: 'row2', row2Scrollable: true })));
};
const Content = (props) => {
    var _a, _b;
    const { onDataChange } = props;
    const [template, setTemplate] = React.useState();
    const [reload, setReload] = React.useState();
    const alertServiceClient = useAlertServiceClient();
    const alertConfig = useAlertConfig();
    const userState = useUserState();
    const settings = useSelector((state) => (userState ? getSettings(state) : undefined));
    const [shouldGenerateUniqueIdentifier, setShouldGenerateUniqueIdentifier] = React.useState((alertConfig === null || alertConfig === void 0 ? void 0 : alertConfig.features) && ((_a = alertConfig.features) === null || _a === void 0 ? void 0 : _a.generateUniqueIdentifier)
        ? alertConfig.features.generateUniqueIdentifier
        : defaultFeatureFlags.notifications.generateUniqueIdentifier);
    const handleDataChange = React.useCallback((data, userRight) => {
        onDataChange(data, userRight);
    }, [onDataChange]);
    React.useEffect(() => {
        if (props.alertId) {
            alertServiceClient
                .getAlert(props.alertId)
                .then((response) => {
                var _a, _b, _c;
                const alertType = response.status === 'template' || response.status === 'draft' ? response.status : 'draft';
                const formData = mapToAlertWizardFormData(Object.assign({}, response), alertConfig, alertType);
                if (props.mode === 'notification' && props.templateVariables) {
                    applyTemplateVariablesToFormData(formData, props.templateVariables);
                }
                if (formData.phinOptions && ((_a = formData.phinOptions) === null || _a === void 0 ? void 0 : _a.uniqueIdentifier) && shouldGenerateUniqueIdentifier === false) {
                    formData.phinOptions.uniqueIdentifier = undefined;
                }
                updateNotificationSenderIfNeeded(userState, formData);
                handleDataChange(Object.assign(Object.assign({}, formData), { recipients: ((_b = props.partialAlert) === null || _b === void 0 ? void 0 : _b.recipients) ? props.partialAlert.recipients : formData.recipients, searchData: (_c = props.partialAlert) === null || _c === void 0 ? void 0 : _c.searchData }), response.right);
                if (props.mode === 'template') {
                    setTemplate(response);
                }
                else {
                    setReload({}); // TODO unable to see changes for props.wizardData.current if edit draft notification is selected
                }
            })
                .catch((error) => {
                handleErrorSnackbar(error);
            });
        }
        // eslint-disable-next-line
    }, [alertConfig, props.mode, userState, alertServiceClient, props.alertId, (_b = props.partialAlert) === null || _b === void 0 ? void 0 : _b.recipients]);
    if (!props.step) {
        return null;
    }
    if (props.step === 'templates') {
        return (React.createElement(TemplateCards, { onAlertCreate: (id) => {
                props.setAlertId(id);
                if (!id) {
                    props.onDataChange(Object.assign(Object.assign({}, props.wizardData.current), { responseOptions: [{ text: settings === null || settings === void 0 ? void 0 : settings.notificationOptions.responseOption }] }));
                }
                props.onStepChange('wizard');
            }, onReviewAndSendClick: (id) => {
                props.setAlertId(id);
                props.onStepChange('review');
            }, templateVariables: props.templateVariables, initialFilterData: props.initialFilterData }));
    }
    if (props.step === 'wizard' && props.wizardData.current) {
        return (React.createElement(AlertWizard, { key: reload, mode: props.mode, alert: props.wizardData.current, template: template, userRight: props.userRight, shouldGenerateUniqueIdentifier: shouldGenerateUniqueIdentifier, onDataChange: handleDataChange, onSubmit: (data) => {
                props.onDataChange(data);
                setShouldGenerateUniqueIdentifier(undefined);
                props.onStepChange('review');
            }, onFirstStepBack: () => {
                props.onStepChange('templates');
                props.setAlertId(undefined);
                props.wizardData.current = undefined;
            } }));
    }
    if (props.step === 'review' && props.wizardData.current) {
        return (React.createElement(AlertReviewForm, { key: reload, mode: props.mode, data: props.wizardData.current, generateUniqueIdentifier: shouldGenerateUniqueIdentifier, onSubmitSuccess: props.onSubmitSuccess, onSaveTemplate: (data) => {
                handleDataChange(data);
                props.onSaveTemplate(data);
            }, onBackToEdit: (data) => {
                if (data) {
                    handleDataChange(data);
                }
                setShouldGenerateUniqueIdentifier(undefined);
                props.onStepChange('wizard');
            } }));
    }
    return React.createElement(React.Fragment, null);
};
function validateWizardRequiredData(data = {}) {
    const errors = {
        step1: {
            label: 'Define Delivery',
            errors: [],
        },
        step2: {
            label: 'Add Message',
            errors: [],
        },
    };
    // Step 1 validation
    if (!data.channels || data.channels.length === 0) {
        errors.step1.errors.push('Delivery Method');
    }
    // Step 2 validation
    if (!data.messageType) {
        errors.step2.errors.push('Message Type');
    }
    if (!data.ownerId || data.ownerId.trim() === '') {
        errors.step2.errors.push('Sender');
    }
    if (!data.subject || data.subject.trim() === '') {
        errors.step2.errors.push('Subject');
    }
    const isValid = errors.step1.errors.length === 0 && errors.step2.errors.length === 0;
    return [isValid, errors];
}
function updateNotificationSenderIfNeeded(userState, formData) {
    formData.ownerId = normalizeNotificationSender(userState, formData.ownerId);
}
